import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import getCompanyTemplateApi from '@/api/get/company/company-template/get-company-template';
import updateCompanyTemplateApi from '@/api/patch/company/company-template/update-company-template';
import Alert from '@/components/alert/alert-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import uploadToTempS3File from '@/utils/upload-to-s3-file';
import { removeUndefinedProperties } from '@/utils/utils';
import { TemplateContextProvider } from './context-provider';
import TemplateForm from './template-form';
import createFlatOptions, { recursiveSetOptionOrder } from './utils';

/**
 * Add template component.
 * @returns {JSX.Element} The rendered Template Add page component.
 */
const TemplateEdit = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id, templateId } = useParams();
  const [prices, setPrices] = useState([]);
  const [options, setOptions] = useState([]);
  const [templateData, setTemplateData] = useState({});

  const contextValue = {
    prices,
    options,
    setOptions,
    setPrices,
    uniqueContextId: Symbol('product-options-context'),
  };
  const [updateTemplateApi, { loading: editTemplateLoading }] = useMutation(
    updateCompanyTemplateApi,
  );

  const onUpdateTemplate = async (
    values,
    shouldNavigate = true,
    onRefresh = () => {},
  ) => {
    try {
      // update template
      setLoading(true);

      const {
        categories: categoriesOriginal,
        templateGallery: templateGalleryOriginal,
        templateDocuments: templateDocumentsOriginal,
        templateLinks: templateLinksOriginal,
        templateOptions: templateOptionsOriginal,
      } = templateData ?? {};

      const {
        template,
        categories,
        templateGallery,
        templateDocuments,
        templateLinks,
        templateOptions,
      } = values;

      delete template.id;
      delete template.created_at;
      delete template.updated_at;

      if (template?.thumbnail?.file) {
        const { file, fileName, fileType } = values.template.thumbnail;
        await uploadToTempS3Base64(
          file,
          `image-upload/${fileName}`,
          fileType,
        ).then((data) => {
          template.thumbnail = `${data.id}`;
        });
      }

      const addCategories = categories
        ?.filter(
          (c1) => !categoriesOriginal?.some((c2) => c1 === c2.category_id),
        )
        .map((a) => a); // adding by category id
      const removeCategories = categoriesOriginal
        ?.filter((c1) => !categories?.some((c2) => c1.category_id === c2))
        ?.map((c) => c.category_id);

      const removeGallery = templateGalleryOriginal
        ?.filter((c1) => !templateGallery?.some((c2) => c1.id === c2.id))
        ?.map((c) => c.id);

      const newGallery = templateGallery
        ?.filter(
          (c1) => !templateGalleryOriginal?.some((c2) => c1.id === c2.id),
        )
        ?.map(async (gallery, index) => {
          const { file } = gallery;
          if (file) {
            return uploadToTempS3File(file, `image-upload/${file.name}`).then(
              (data) => {
                const fileKey = data?.id;
                const updatedGallery = {
                  image: fileKey,
                  order: templateGalleryOriginal.length + (index + 1),
                };
                return updatedGallery;
              },
            );
          }
        });
      const addGallery = await Promise.all(newGallery);

      const newDocuments = templateDocuments
        ?.filter(
          (c1) => !templateDocumentsOriginal?.some((c2) => c1.id === c2.id),
        )
        .map(async (doc) => {
          const { fileName, file } = doc.file;
          return uploadToTempS3File(file, `document-upload/${fileName}`).then(
            (data) => {
              const newDoc = {
                document: data.id,
                name: doc.name,
              };

              return newDoc;
            },
          );
        });
      const addDocuments = await Promise.all(newDocuments);

      const removeDocuments = templateDocumentsOriginal
        ?.filter((c1) => !templateDocuments?.some((c2) => c1.id === c2.id))
        ?.map((c) => c.id);

      const addLinks = templateLinks?.filter(
        (c1) => !templateLinksOriginal?.some((c2) => c1.id === c2.id),
      );
      const removeLinks = templateLinksOriginal
        ?.filter((c1) => !templateLinks?.some((c2) => c1.id === c2.id))
        ?.map((c) => c.id);

      const newOptions = createFlatOptions(
        templateOptions,
        templateOptionsOriginal,
      );

      const payload = {
        template,
        categories: { add: addCategories, remove: removeCategories },
        templateGallery: { add: addGallery, remove: removeGallery },
        templateDocuments: { add: addDocuments, remove: removeDocuments },
        templateLinks: { add: addLinks, remove: removeLinks },
        templateOptions: newOptions,
      };

      updateTemplateApi(id, templateId, removeUndefinedProperties(payload))
        .then(async ({ data }) => {
          if (shouldNavigate) {
            navigate(-1);
          }

          if (data?.propagation === false) {
            Alert({
              icon: 'error',
              text: 'Template updated successfully, but propagation failed',
            });
          } else {
            Alert({
              icon: 'success',
              text: 'Template updated successfully',
            });
          }

          const { data: updatedTemplateData } = await getCompanyTemplateApi(
            id,
            templateId,
          );

          setTemplateData(JSON.parse(JSON.stringify(updatedTemplateData)));

          updatedTemplateData.templateOptions = recursiveSetOptionOrder(
            updatedTemplateData?.templateOptions ?? [],
          );

          updatedTemplateData.categories =
            updatedTemplateData?.categories?.map((c) => c.category_id) ?? [];

          setLoading(false);
          onRefresh(updatedTemplateData);
        })
        .catch((err) => {
          setLoading(false);
          Alert({
            icon: 'error',
            text: 'Template failed to update',
            message: err.response?.data?.message,
          });
        });
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <TemplateContextProvider value={contextValue}>
      <FormWrapper
        defaultValues={async () => {
          const { data } = await getCompanyTemplateApi(id, templateId);

          setTemplateData(JSON.parse(JSON.stringify(data)));

          data.templateOptions = recursiveSetOptionOrder(
            data?.templateOptions ?? [],
          );

          data.categories = data?.categories?.map((c) => c.category_id) ?? [];
          return data;
        }}
        onSubmit={async (values) => {
          await onUpdateTemplate(values);
        }}
      >
        <TemplateForm
          type="edit"
          isLoading={editTemplateLoading || loading}
          onApply={(values, onRefresh) => {
            onUpdateTemplate(values, false, onRefresh);
          }}
        />
      </FormWrapper>
    </TemplateContextProvider>
  );
};

export default TemplateEdit;
